/**
 * ANA-R3-TILE-03 — read + write the per-user Analysis Tiles settings
 * persisted under `auth/v1@5.6.0`'s `LysePreferences.tiles` block.
 *
 *   maxZoomPercent — % cap on per-tile crop magnification (Part A). The
 *     tile crop zooms to fit the padded defect, then clamps to this cap, so
 *     tiny defects fill the tile up to the cap instead of hyper-zooming.
 *   tileSize       — grid cell-size preset (Part B): small / medium / large.
 *
 * Mirrors `useViewerSettings`: reads come from `useLysePreferencesQuery()`
 * (shared TanStack-Query cache), writes go through the debounced/optimistic
 * `useUpdateLysePreferencesMutation()`. The FULL `tiles` block is sent on
 * every change so the BE's top-level last-write-wins PATCH never lands a
 * half-populated row.
 *
 * Client-side defaults + clamps live here (`TILE_MAX_ZOOM` 2000, default
 * 1000): even if a forward-compat blob ever slips an out-of-range value
 * past the lenient schema, the setters re-clamp before PATCHing.
 */
import { useCallback } from "react";
import {
  useLysePreferencesQuery,
  useUpdateLysePreferencesMutation,
  type TilePreferences,
  type TileSize,
} from "../../preferences/index.js";

export interface UseTileSettingsApi {
  /** Effective tiles prefs. Never undefined — defaults applied client-side. */
  tiles: TilePreferences;
  /** Set `maxZoomPercent` (clamps to [100, 2000] before PATCHing). */
  setMaxZoomPercent: (next: number) => void;
  /** Set the tile-size preset. */
  setTileSize: (next: TileSize) => void;
  /** True while the debounced PATCH is in flight. */
  isSaving: boolean;
}

export const TILE_MIN_ZOOM = 100;
export const TILE_MAX_ZOOM = 2000;

function clamp(n: number, lo: number, hi: number): number {
  if (n < lo) return lo;
  if (n > hi) return hi;
  return n;
}

export function useTileSettings(): UseTileSettingsApi {
  const { prefs } = useLysePreferencesQuery();
  const { update, isSaving } = useUpdateLysePreferencesMutation();

  // `prefs.tiles` is defaulted by the zod schema — guaranteed present here.
  const tiles: TilePreferences = prefs.tiles;

  const setMaxZoomPercent = useCallback(
    (next: number) => {
      const clamped = clamp(Math.round(next), TILE_MIN_ZOOM, TILE_MAX_ZOOM);
      update({
        tiles: {
          maxZoomPercent: clamped,
          tileSize: tiles.tileSize,
        },
      });
    },
    [update, tiles.tileSize],
  );

  const setTileSize = useCallback(
    (next: TileSize) => {
      update({
        tiles: {
          maxZoomPercent: tiles.maxZoomPercent,
          tileSize: next,
        },
      });
    },
    [update, tiles.maxZoomPercent],
  );

  return { tiles, setMaxZoomPercent, setTileSize, isSaving };
}
